import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StreamingMessages } from "./StreamingMessages";
import { ComparisonModal } from "@/features/product-comparison/components/ComparisonModal";
import { MobileChatInput } from "./MobileChatInput";
import {
  ChatErrorBoundary,
  MessageErrorBoundary,
  InputErrorBoundary,
  LoadingSpinner,
} from "@/components/common";
import { useBreadcrumb } from "@/features/breadcrumb";
import { useCurrentChatId } from "@/hooks";
import { useSidebarConversations } from "@/features/conversations";
import { usePageTitle } from "@/store/use-page-title-store";
import { useStreamingChat } from "../hooks/use-streaming-chat";
import {
  draftChatKeyFromRouteId,
  hasIdentifiedCompletedDraft,
  useStreamingStore,
} from "../store/streaming-store";
import { useLLMSelection } from "@/features/llm-selection";
import { useChatStore } from "../store/chat-store";
import { useChatAccessStore } from "../store/chat-access-store";
import { useChatScope } from "../scope/chat-scope-context";
import { registerVisibleChat } from "../scope/visible-chats";
import type { ChatAttachment } from "../types";
import { Info } from "lucide-react";

/**
 * Konuşma ekranı. İki yüzeyde çalışır (bkz. ChatScopeContext):
 *   page — /chat/:id; kimlik route'tan, ilk mesaj location.state'ten
 *   dock — modül sağ paneli; kimlik ve ilk mesaj scope'tan, sayfa
 *          efektleri (breadcrumb, başlık, URL doğrulaması) kapalı
 */
export const StreamingChat = () => {
  const { t } = useTranslation();
  const scope = useChatScope();
  const isDock = scope?.surface === "dock";
  const { id: routeId } = useParams<{ id: string }>();
  // Dock'ta route param'ı başka bir şeyin (ör. wizard model id) olabilir.
  // `draft` henüz konuşması olmayan ilk turdur.
  const id = isDock
    ? (scope?.chatId ?? undefined)
    : (draftChatKeyFromRouteId(routeId) ?? routeId);
  const navigate = useNavigate();
  const location = useLocation();
  const activeChatId = useCurrentChatId();
  const { conversations } = useSidebarConversations();
  const { setDynamicLabel } = useBreadcrumb();
  const { setPageTitle, clearPageTitle } = usePageTitle();

  // canEdit / isPublic state (set by StreamingMessages)
  const chatAccess = useChatAccessStore((state) =>
    id ? state.byChatId[id] : undefined
  );
  const canEdit = chatAccess?.canEdit;

  const shareTokenFromUrl = useRef<string | null>(null);
  const isSharedView = useRef(false);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    shareTokenFromUrl.current = params.get("token");
    isSharedView.current = !!shareTokenFromUrl.current;
  }, [location.search]);

  // Model selection
  const { selectedModel, selectedModelName } = useLLMSelection();

  // 🆕 Chat store - selectedAttachments için (global state)
  const {
    selectedAttachments,
    setSelectedAttachments,
    clearSelectedAttachments,
  } = useChatStore();

  // Streaming state & actions
  const [inputMessage, setInputMessage] = useState("");
  const { sendStreamingMessage, stopStreaming, drainQueuedMessages } =
    useStreamingChat();
  const ensureSession = useStreamingStore((state) => state.ensureSession);
  const setPendingUserMessage = useStreamingStore(
    (state) => state.setPendingUserMessage
  );
  const enqueueMessage = useStreamingStore((state) => state.enqueueMessage);
  const removeQueuedMessage = useStreamingStore(
    (state) => state.removeQueuedMessage
  );
  const markChatSeen = useStreamingStore((state) => state.markChatSeen);

  // Sırada bekleyen mesaj sayısı (stop butonunun davranışını belirler)
  const queuedCountInThisChat = useStreamingStore((state) => {
    if (!id) return 0;
    return state.sessionsByChat[id]?.queuedMessages.length ?? 0;
  });

  // Composer'ın "meşgul" hâli (yılan + stop butonu): iki üretim arasında
  // status bir anlığına "completed" olur ve drain kuyruktaki mesajı ancak
  // ondan sonra gönderir. Yalnızca "streaming"e bakmak yılanı bu boşlukta
  // unmount edip animasyonu sıfırdan başlatıyordu. Kuyruk doluysa boşluk da
  // meşguldür; hata kuyruğu dondurduysa (drain durur) meşgul sayılmaz.
  const isBusyInThisChat = useStreamingStore((state) => {
    if (!id) return false;
    const session = state.sessionsByChat[id];
    if (!session) return false;
    if (session.status === "streaming") return true;
    return session.queuedMessages.length > 0 && session.status !== "error";
  });

  // Stop butonu: sırada mesaj varken önce en son ekleneni geri alır
  // (üretim sürmeye devam eder); kuyruk boşsa aktif üretimi durdurur.
  const handleStopOrDequeue = () => {
    if (!id) return;
    const queued = useStreamingStore.getState().getSession(id).queuedMessages;
    if (queued.length > 0) {
      removeQueuedMessage(id, queued[queued.length - 1].id);
      return;
    }
    stopStreaming(id);
  };

  // 🆕 Landing page'den gelen pending mesajı takip et
  const hasProcessedPendingMessage = useRef(false);

  // Oturumu garanti et, okunmadı işaretini kaldır ve sohbeti "görünür" kaydet
  // (dock'ta açıkken tamamlanan yanıt okunmadı sayılmasın)
  useEffect(() => {
    if (!id) return;
    ensureSession(id);
    markChatSeen(id);
    return registerVisibleChat(id);
  }, [id, ensureSession, markChatSeen]);

  // Streaming message handler
  const handleStreamingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const attachmentsToSend = selectedAttachments;

    // 🆕 Validation: Mesaj veya dosya en az biri olmalı
    if (!inputMessage.trim() && attachmentsToSend.length === 0) {
      return;
    }

    if (!id) return;

    // 🆕 Eğer sadece dosya varsa, boş mesaj yerine placeholder gönder
    const messageToSend =
      inputMessage.trim() ||
      (attachmentsToSend.length > 0 ? t("chat.fileAttachedMessage") : "");
    // selectedModelName persist edilen alan; obje fallback'i de "name" olmalı
    const modelToUse = selectedModelName || selectedModel?.name || undefined;

    // Optimistic önizleme için görsel attachment URL'leri
    const attachmentImageUrls = attachmentsToSend
      .filter((attachment) => attachment.mimeType.startsWith("image/"))
      .map((attachment) => attachment.url);

    // Aktif üretim sürerken (veya hata sonrası bekleyen kuyruk varken)
    // mesajı kuyruğa al; mevcut stream bitince otomatik sırayla gönderilir.
    const session = useStreamingStore.getState().getSession(id);
    const isStreamingNow = session.status === "streaming";
    const hasQueuedMessages = session.queuedMessages.length > 0;
    const isWaitingForHistory = hasIdentifiedCompletedDraft(session);

    if (isStreamingNow || hasQueuedMessages || isWaitingForHistory) {
      setInputMessage("");
      clearSelectedAttachments();
      enqueueMessage(id, {
        message: messageToSend,
        attachments: attachmentsToSend,
        attachmentImageUrls,
        model: modelToUse,
      });
      // Streaming yoksa (ör. hata sonrası kalan kuyruk) draini burada başlat
      if (!isStreamingNow) {
        void drainQueuedMessages(id);
      }
      return;
    }

    try {
      // Yeni mesaj için hazırlık: input ve attachment'ları hemen temizle
      // (stream bitene kadar composer'da asılı kalmasınlar)
      setInputMessage("");
      clearSelectedAttachments();
      setPendingUserMessage(
        id,
        messageToSend,
        `pending_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        attachmentImageUrls
      );

      // 🆕 Attachments'ı streaming request'e geç
      await sendStreamingMessage(
        messageToSend,
        id,
        modelToUse,
        attachmentsToSend.length > 0 ? attachmentsToSend : undefined
      );
    } catch (apiError: any) {
      console.error("❌ Mesaj gönderme hatası:", apiError);
      // Hata durumunda input'u ve ekleri geri yükle, pending mesajı temizle
      setInputMessage(messageToSend);
      if (attachmentsToSend.length > 0) {
        setSelectedAttachments(attachmentsToSend);
      }
      setPendingUserMessage(id, null, null);
    }
  };

  // URL validation - ID yoksa veya invalid ise home'a yönlendir (yalnızca page)
  useEffect(() => {
    if (isDock) return;
    if (draftChatKeyFromRouteId(routeId)) return;
    if (!routeId || routeId.length !== 24) {
      navigate("/");
      return;
    }
  }, [routeId, isDock, navigate]);

  // 🆕 İlk mesajı işle ve streaming başlat.
  // page: landing composer'ı location.state ile taşır
  // dock: composer scope.pending ile taşır (URL'e dokunulmaz)
  const scopePending = scope?.pending;
  const consumePending = scope?.consumePending;
  useEffect(() => {
    // Sadece bir kez işle (mount'ta)
    if (hasProcessedPendingMessage.current) {
      return;
    }

    let pendingMessage: string | undefined;
    let pendingAttachments: ChatAttachment[] | undefined;
    if (isDock) {
      if (scopePending && scopePending.chatId === id) {
        pendingMessage = scopePending.message;
        pendingAttachments = scopePending.attachments;
      }
    } else {
      pendingMessage = (location.state as any)?.pendingMessage;
      pendingAttachments = (location.state as any)?.pendingAttachments as
        | ChatAttachment[]
        | undefined;
    }

    if (pendingMessage && id) {
      hasProcessedPendingMessage.current = true;

      // Tekrar kullanılmasın: page'de state temizle, dock'ta scope'a bildir
      if (isDock) consumePending?.();
      else navigate(location.pathname, { replace: true, state: {} });

      // Model bilgisini al (persist edilen alan "name")
      const modelToUse = selectedModelName || selectedModel?.name || undefined;

      // Optimistic önizleme için görsel attachment URL'leri
      const pendingImageUrls = (pendingAttachments ?? [])
        .filter((attachment) => attachment.mimeType?.startsWith("image/"))
        .map((attachment) => attachment.url);

      // Streaming başlat
      const startStreaming = async () => {
        try {
          // Pending user message'ı store'a ekle
          setPendingUserMessage(
            id,
            pendingMessage,
            `pending_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
            pendingImageUrls
          );

          // 🆕 Streaming request'i attachments ile başlat
          await sendStreamingMessage(
            pendingMessage,
            id,
            modelToUse,
            pendingAttachments
          );
        } catch (error) {
          console.error(
            "Landing page mesajı ile streaming başlatma hatası:",
            error
          );
          // Hata durumunda pending mesajı temizle
          setPendingUserMessage(id, null, null);
        }
      };

      // Component tam yüklendikten sonra streaming başlat
      // Kısa bir delay ile component'in tam mount olmasını bekle
      setTimeout(() => {
        startStreaming();
      }, 100);
    }
  }, [
    location.state,
    location.pathname,
    isDock,
    scopePending,
    consumePending,
    id,
    selectedModelName,
    selectedModel,
    sendStreamingMessage,
    setPendingUserMessage,
    navigate,
  ]);

  // Breadcrumb & Page Title yönetimi (yalnızca page; dock sayfanın
  // başlığına dokunmaz)
  useEffect(() => {
    if (isDock) return;
    if (activeChatId) {
      const activeConversation = conversations.find(
        (conversation) => conversation._id === activeChatId
      );
      if (activeConversation) {
        setDynamicLabel(`/chat/${id}`, activeConversation.name);
        setPageTitle(activeConversation.name, "");
        return () => clearPageTitle();
      }
    }
  }, [
    isDock,
    activeChatId,
    conversations,
    id,
    setDynamicLabel,
    setPageTitle,
    clearPageTitle,
  ]);

  // Loading state - ID validation sırasında
  if (!id) {
    return (
      <div className="h-full flex items-center justify-center">
        <LoadingSpinner size="lg" text={t("chat.loading")} />
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Content Area - max-w-5xl container içinde */}
      <div className="flex-1 w-full max-w-5xl mx-auto flex flex-col min-h-0">
        <ChatErrorBoundary
          onError={(error) => {
            console.error("🚨 Streaming Chat Error:", error);
            // Send to monitoring service
          }}
        >
          <div className="h-full flex flex-col">
            {/* Messages Area */}
            <div className="flex-1 min-h-0">
              <MessageErrorBoundary>
                <StreamingMessages />
                <ComparisonModal />
              </MessageErrorBoundary>
            </div>

            {/* Input Area */}
            <div className="w-full">
              <InputErrorBoundary>
                {canEdit === false ? (
                  <div className="w-full max-w-3xl mx-auto pb-4 sm:pb-2">
                    <div className="mx-3 sm:mx-0 rounded-2xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 shadow-sm">
                      <div className="flex items-start gap-3 p-3 sm:p-4">
                        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600/10 dark:bg-indigo-500/15 border border-indigo-200/60 dark:border-indigo-900/40 flex-none">
                          <Info className="h-4 w-4 text-indigo-700 dark:text-indigo-200" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-text-primary dark:text-gray-100">
                            {t("chat.shareView.readOnlyInputTitle")}
                          </div>
                          <div className="mt-1 text-sm text-text-secondary dark:text-gray-300">
                            {t("chat.shareView.readOnlyInputDescription")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <MobileChatInput
                    customMessage={inputMessage}
                    customSetMessage={setInputMessage}
                    customHandleSubmit={handleStreamingSubmit}
                    customIsLoading={isBusyInThisChat}
                    customIsSubmitting={isBusyInThisChat}
                    customHandleStop={handleStopOrDequeue}
                    customStopLabel={
                      queuedCountInThisChat > 0
                        ? t("chat.queue.removeLast")
                        : undefined
                    }
                  />
                )}
              </InputErrorBoundary>
            </div>
          </div>
        </ChatErrorBoundary>
      </div>
    </div>
  );
};

export default StreamingChat;
